import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Lenis from "lenis";
import { MapPin, Users, Globe, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { Hero } from "@/components/landing/Hero";
import { EditorialMarquee } from "@/components/landing/Marquee";
import { Benefits } from "@/components/landing/Benefits";
import { Offer } from "@/components/landing/Offer";
import { StickyCTA } from "@/components/landing/StickyCTA";
import { SignupDialog } from "@/components/landing/SignupDialog";

const Nav = ({ stats }) => (
  <header data-testid="landing-nav" className="fixed top-0 inset-x-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
      <div>
        <img src="/assets/logo.png" alt="PHYSIMETRY — 3D Body Scan Studio" data-testid="nav-logo" className="h-10 w-auto" />
      </div>
      <div data-testid="nav-counter" className="flex items-center gap-2 border border-volt/40 bg-volt/10 px-4 py-2">
        <span className="h-2 w-2 rounded-full bg-volt animate-pulse" />
        <span className="font-display text-sm tracking-[0.2em]">{stats.founding_count}/100</span>
      </div>
    </div>
  </header>
);

const Footer = ({ stats }) => (
  <footer data-testid="landing-footer" className="border-t border-white/10 px-6 md:px-12 py-12">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border border-gold/25 bg-gold-soft px-6 py-4 mb-10" data-testid="footer-info-strip">
        <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80"><MapPin className="h-4 w-4 text-gold" /> Aventura, FL</span>
        <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80" data-testid="footer-spots-claimed"><Users className="h-4 w-4 text-gold" /> {stats.founding_count} spots claimed</span>
        <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80"><Globe className="h-4 w-4 text-gold" /> physimetry.com</span>
        <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80"><Instagram className="h-4 w-4 text-gold" /> @physimetry</span>
      </div>
      <div className="flex flex-col md:flex-row justify-between gap-6">
        <div>
          <img src="/assets/logo.png" alt="PHYSIMETRY — 3D Body Scan Studio" data-testid="footer-logo" className="h-12 w-auto" />
          <p className="text-xs text-white/50 mt-2 uppercase tracking-[0.2em]">Aventura, Florida — Opening October 30, 2026</p>
          <p className="text-xs text-white/40 mt-1 uppercase tracking-[0.2em]">Future locations — 2027 &amp; 2028</p>
        </div>
        <a href="/admin" data-testid="footer-admin-link" className="text-xs uppercase tracking-[0.25em] text-white/40 hover:text-volt transition-colors self-start md:self-end">
          Admin
        </a>
      </div>
      <p className="mt-10 text-[11px] leading-relaxed text-white/35 max-w-3xl">
        Disclaimer: PHYSIMETRY scans are for wellness and fitness purposes only. They are not intended to diagnose, treat, cure, or prevent any disease.
      </p>
    </div>
  </footer>
);

export default function Landing() {
  const [stats, setStats] = useState({ founding_count: 0, overflow_count: 0, total: 0, spots_left: 100, is_full: false, limit: 100 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [joinOpen, setJoinOpen] = useState(() => searchParams.get("join") === "1");

  useEffect(() => {
    if (searchParams.get("join") === "1") {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const refreshStats = useCallback(async () => {
    try {
      const res = await api.get("/stats");
      setStats(res.data);
    } catch (e) {
      // keep last known stats
    }
  }, []);

  useEffect(() => {
    refreshStats();
    const t = setInterval(refreshStats, 10000);
    return () => clearInterval(t);
  }, [refreshStats]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div data-testid="landing-page" className="relative min-h-screen bg-[#050505] text-white font-body overflow-x-clip pb-28">
      <div className="noise-overlay" aria-hidden="true" />
      <Nav stats={stats} />
      <Hero stats={stats} onJoin={() => setJoinOpen(true)} />
      <EditorialMarquee />
      <Benefits />
      <Offer stats={stats} onJoin={() => setJoinOpen(true)} />
      <Footer stats={stats} />
      <StickyCTA stats={stats} onJoin={() => setJoinOpen(true)} />
      <SignupDialog open={joinOpen} onOpenChange={setJoinOpen} isFull={stats.is_full} onSuccess={refreshStats} />
    </div>
  );
}
